import { useCallback, useMemo, useState } from 'react';
import type { Query } from './api';

/**
 * State for a filterable, paged list.
 *
 * Filters have a draft (what the form shows) and an applied value (what the query uses).
 * As in the reference, the list only reloads when the user presses Search.
 */
export function useListState<F extends Query>(
  initial: F,
  initialPageSize = 10,
  initialSort?: string,
) {
  const [draft, setDraft] = useState<F>(initial);
  const [applied, setApplied] = useState<F>(initial);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [sort, setSortState] = useState<string | undefined>(initialSort);

  const setField = useCallback(<K extends keyof F>(key: K, value: F[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const search = useCallback(() => {
    setApplied(draft);
    setPage(1);
  }, [draft]);

  const reset = useCallback(() => {
    setDraft(initial);
    setApplied(initial);
    setPage(1);
    // `initial` is a constant object literal at each call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1);
  }, []);

  /** Sets the filters and runs the search with them at once (a tile that opens a filtered list). */
  const apply = useCallback((values: F) => {
    setDraft(values);
    setApplied(values);
    setPage(1);
  }, []);

  const setSort = useCallback((value: string) => {
    setSortState(value);
    setPage(1);
  }, []);

  /** Query object for the API call; also a stable React Query key part. */
  const params = useMemo<Query>(
    () => ({ ...applied, page, pageSize, sort }),
    [applied, page, pageSize, sort],
  );

  return {
    draft,
    setField,
    applied,
    search,
    reset,
    apply,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    params,
  };
}
